import {
  FilterButton,
  FilterModal,
  FilterUserModal,
  Searchbar,
} from '@/components'
import { Alert, Grid2, Snackbar, styled } from '@mui/material'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import {
  FilterConfig,
  FilterState,
  RoleData,
  UserData,
} from '@/utilities/types'
import { userManagementFilterConfig } from '@/utilities/constants/filterConfigs'
import { UserManagementTable } from '@/components/table/management/user-management/UserManagementTable'
import { useForm } from '@/utilities'
import { UserManagementForm } from './user-management-form/UserManagementForm'
import { useUserManagementContext } from '@/context/UserManagementContext'
import { useAlert } from '@/utilities/useAlert'
import { RESET_INITIAL_STATE } from '@/constants/Actions'
import FilterIcon from '@/assets/icons/filter.svg'
import { useNavigate } from 'react-router-dom'

const Container = styled('div')({
  width: '100%',
  height: '100%',
  color: '#000',
  background: 'transparent',
})

type CurrentFilterState = FilterState<UserData> | FilterState<RoleData>

const initialFilterState: CurrentFilterState = {
  regularFilters: {},
  dateRanges: {
    createdDate: { start: null, end: null },
    modifiedDate: { start: null, end: null },
  },
  timeRange: { start: null, end: null },
  sort: {
    field: 'id',
    direction: 'asc',
  },
}

interface UserManagementProps {}

export const UserManagement: FC<UserManagementProps> = () => {
  const {
    id,
    users,
    dispatch,
    isLoading,
    optimizedSearchUsers,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    roles,
    userId,
    state,
    mutateUsers,
    totalElem,
    optimizedFilterUsers,
    optimizedFilterRoleUsers,
    handleSort,
    actions: { updateRole },
  } = useUserManagementContext()

  const navigate = useNavigate()
  const role = JSON.parse(localStorage.getItem('role') || '')

  useEffect(() => {
    if (role !== 'Super Admin') {
      navigate('/maintenance-tickets')
    }
  }, [role, navigate])

  const validatedData = useMemo(() => {
    if (!users) return []
    return users
  }, [users])

  const validatedRoles = useMemo(() => {
    if (!roles) return []
    return roles
  }, [roles])

  const { formData, showModal, handleUpdateData, handleCloseModal } = useForm()

  const { handleAlertMessage, ...alertRest } = useAlert()

  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterState, setFilterState] =
    useState<CurrentFilterState>(initialFilterState)

  const handleFilterClick = useCallback(() => setFilterModalOpen(true), [])
  const handleFilterClose = useCallback(() => {
    setFilterModalOpen(false)
  }, [])

  const handleApplyFilter = useCallback((newFilterState: FilterState) => {
    setFilterState(newFilterState)
    setFilterModalOpen(false)
  }, [])

  const handleReset = useCallback(() => {
    setFilterState(initialFilterState)

    optimizedFilterUsers('', '', '', '')
    optimizedFilterRoleUsers('')
    handleFilterClose()
  }, [])

  const handleSearchKeyword = (keyword: string) => {
    optimizedSearchUsers(keyword)
  }

  const handleSuccess = () => {
    handleAlertMessage(state.successMessage, 'success')
    mutateUsers()
    handleCloseModal()
    dispatch({ type: RESET_INITIAL_STATE })
  }

  const handleError = () => {
    handleAlertMessage(state.errorMessage, 'error')
    handleCloseModal()
    dispatch({ type: RESET_INITIAL_STATE })
  }

  useEffect(() => {
    if (state.successMessage) {
      handleSuccess()
    } else if (state.errorMessage) {
      handleError()
    }
  }, [state.success, state.error])

  const renderTable = useMemo(() => {
    const baseProps = {
      filterState,
      isLoading: isLoading,
      error: null,
      onSort: handleSort,
    }

    return (
      <UserManagementTable
        {...baseProps}
        data={validatedData as UserData[]}
        handleUpdate={handleUpdateData}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalElem={totalElem}
        handleSort={handleSort}
      />
    )
  }, [filterState, validatedData, handleSort, isLoading])

  const handleUpdate = (data: any) => {
    if (data.roles?.length > 1) {
      handleCloseModal()
      return handleAlertMessage('Only one role can be submitted.', 'error')
    }
    const matchingRoleIds = validatedRoles
      .filter((role) => data.roles.includes(role.name))
      .map((role) => role.id)

    let newData = {
      sourceUserId: data.sourceUserId,
      roleIds: matchingRoleIds,
    }

    updateRole(newData)(dispatch)
  }

  return (
    <Container>
      <Grid2
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Searchbar handleSearchKeyword={handleSearchKeyword} keyword={''} />
        <Grid2 display={'flex'} gap={2}>
          <FilterButton
            icon={FilterIcon}
            onClick={handleFilterClick}
            width={16}
            height={16}
          />
        </Grid2>
      </Grid2>
      <Grid2
        container
        display={'flex'}
        flexDirection={'column'}
        marginTop={'1%'}
      >
        {renderTable}
      </Grid2>
      {filterModalOpen && (
        <FilterUserModal<UserData>
          open={filterModalOpen}
          onClose={handleFilterClose}
          config={
            userManagementFilterConfig(validatedRoles) as FilterConfig<UserData>
          }
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedFilterUsers={optimizedFilterUsers}
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleReset}
          optimizedFilterRoleUsers={optimizedFilterRoleUsers}
        />
      )}
      {showModal && (
        <UserManagementForm
          open={showModal}
          onClose={handleCloseModal}
          onUpdate={handleUpdate}
          data={formData}
          rolesData={validatedRoles}
        />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={alertRest.showAlert}
        onClose={alertRest.handleCloseAlert}
        autoHideDuration={3000}
      >
        <Alert
          onClose={alertRest.handleCloseAlert}
          elevation={6}
          variant="filled"
          severity={alertRest.alertSeverity}
        >
          {alertRest.alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}
